import * as React from 'react'

import { comparer, computed } from 'mobx'
import { useMap } from 'react-map-gl/mapbox'

import { placeMarkupTextLayers } from '../MapBoxViewer/mapboxLayerOrder'
import {
  registerMarkupTextLayer,
  unregisterMarkupTextLayer,
} from '../MapBoxViewer/markupTextLayerIds'
import { ITextMarkupInput } from './markupTextStyle'
import { ITextMarkupItem, TextMarkupGLLayer } from './TextMarkupGLLayer'
import {
  areTextMarkupFacesSettled,
  loadTextMarkupFaces,
  registerTextMarkupSpecs,
} from './textMarkupRegistry'

// The texts ready to draw, in draw order: those whose font faces have settled, measured once
// (the rest follow when their faces settle, never with fallback metrics). Call from an
// observer, which re-renders as faces settle.
export function useTextMarkupItems(
  inputs: ITextMarkupInput[],
): ITextMarkupItem[] {
  const specs = React.useMemo(() => inputs.map(input => input.spec), [inputs])
  React.useEffect(() => loadTextMarkupFaces(specs), [specs])
  // Computed, so the observer tracks the face table and the check runs again only when it
  // changes: a mount re-renders on every zoom frame, and checking every text costs ms.
  const settled = React.useMemo(
    () =>
      computed(() => specs.map(areTextMarkupFacesSettled), {
        equals: comparer.structural,
      }),
    [specs],
  ).get()

  return React.useMemo<ITextMarkupItem[]>(() => {
    const ready = specs.flatMap((spec, i) =>
      settled[i] && spec.text !== '' ? [i] : [],
    )
    const ids = registerTextMarkupSpecs(ready.map(i => specs[i]))
    return ready.map((i, k) => ({
      id: inputs[i].id,
      specId: ids[k],
      lng: inputs[i].lng,
      lat: inputs[i].lat,
      rotation: inputs[i].rotation,
    }))
  }, [inputs, specs, settled])
}

interface IProps {
  idPrefix: string
  items: ITextMarkupItem[]
  opacity: number
  // Ringed as the copy-select source.
  highlightId?: string | null
  sizeScale?: number
}

const isStyleReady = (map: mapboxgl.Map): boolean =>
  !!(map as unknown as { style?: { _loaded?: boolean } }).style?._loaded

// Mounts one TextMarkupGLLayer at the top of the markup geometry and feeds it the items.
const MarkupTextLayer: React.FC<IProps> = ({
  idPrefix,
  items,
  opacity,
  highlightId = null,
  sizeScale = 1,
}) => {
  const { current: mapRef } = useMap()
  const map = mapRef?.getMap()
  const layer = React.useMemo(
    () => new TextMarkupGLLayer(`${idPrefix}-text`),
    [idPrefix],
  )

  React.useEffect(() => {
    if (!map) return undefined
    registerMarkupTextLayer(map, layer.id)
    const add = (): void => {
      if (!isStyleReady(map) || map.getLayer(layer.id)) return
      map.addLayer(layer)
      placeMarkupTextLayers(map)
    }
    add()
    // setStyle drops custom layers.
    map.on('style.load', add)
    return () => {
      map.off('style.load', add)
      unregisterMarkupTextLayer(map, layer.id)
      if (isStyleReady(map) && map.getLayer(layer.id)) {
        map.removeLayer(layer.id)
      } else {
        // A style reload dropped it without onRemove, or the style is still loading.
        layer.detach()
      }
    }
  }, [map, layer])

  // A layout effect: items land in the same commit as any DOM change made with them.
  React.useLayoutEffect(() => {
    layer.setItems(items, opacity, highlightId, sizeScale)
  }, [layer, items, opacity, highlightId, sizeScale])

  return null
}

export default React.memo(MarkupTextLayer)
